import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Sparkles, Send, Loader2, Trash2, Plus, MessageCircle, Lightbulb, Lock, Brain, X
} from 'lucide-react';
import { toast } from 'sonner';
import { authFetch, authJsonFetch } from '../lib/api';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const SUGGESTIONS = [
  'What 2026 schools haven\'t got a booking_date yet?',
  'Which orgs were updated this week and what changed?',
  'Show me dispatches that are overdue.',
  'Which clubs are due for a follow-up — no contact in 30+ days?',
  'How does this year compare to last year by marketing_status?',
];

export default function AssistMePage() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [modelInfo, setModelInfo] = useState(null); // {provider, model, temperature, source}
  const [modelOptions, setModelOptions] = useState([]);
  const [modelChoice, setModelChoice] = useState('default');
  const scrollRef = useRef(null);

  const loadSessions = useCallback(async () => {
    const res = await authFetch(`${API}/assist-me/sessions?limit=30`);
    if (res.ok) {
      const data = await res.json();
      setSessions(data.sessions || []);
    } else if (res.status === 403) {
      toast.error('Assist Me is not enabled — turn it on in Settings → Feature Flags.');
    }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Fetch the configured AI provider/model so the header shows what's
  // currently powering Assist Me — operator can switch on demand from
  // the model dropdown in the header if hitting 504s on a slow model.
  useEffect(() => {
    (async () => {
      try {
        const [curRes, optRes] = await Promise.all([
          authFetch(`${API}/ai/current-model`),
          authFetch(`${API}/assist-me/model-options`),
        ]);
        if (curRes.ok) {
          const data = await curRes.json();
          setModelInfo({
            provider: data.provider,
            model: data.model,
            temperature: null,
            source: 'system_config',
          });
        }
        if (optRes.ok) {
          const data = await optRes.json();
          setModelOptions(data.options || []);
        }
      } catch (_e) { /* non-fatal */ }
    })();
  }, []);

  // Persist user's model choice across page reloads (per-browser)
  useEffect(() => {
    const saved = localStorage.getItem('assist_me_model_choice');
    if (saved) setModelChoice(saved);
  }, []);
  useEffect(() => {
    localStorage.setItem('assist_me_model_choice', modelChoice);
  }, [modelChoice]);

  // Auto-scroll the transcript to the bottom whenever messages change
  // or the "Thinking…" indicator appears. We wait one rAF so the new
  // <div> has actually painted before we measure scrollHeight, otherwise
  // the very first stream of a long reply ends up cut off above the fold.
  // We also respect the operator: if they've manually scrolled up to read
  // older history (>120px from the bottom), we don't yank them back —
  // instead a "↓ New messages" pill appears so they can opt in.
  const [autoFollow, setAutoFollow] = useState(true);
  const [hasNewBelow, setHasNewBelow] = useState(false);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    if (!scrollRef.current) return;
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior });
      setHasNewBelow(false);
    });
  }, []);

  useEffect(() => {
    if (autoFollow) scrollToBottom('smooth');
    else setHasNewBelow(true);
  }, [messages, sending, autoFollow, scrollToBottom]);

  const onTranscriptScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = distFromBottom < 120;
    setAutoFollow(nearBottom);
    if (nearBottom) setHasNewBelow(false);
  }, []);

  const openSession = async (sid) => {
    setLoadingSession(true);
    setActiveId(sid);
    try {
      const res = await authFetch(`${API}/assist-me/sessions/${sid}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        // Land at the most-recent message when opening a session
        setAutoFollow(true);
        setHasNewBelow(false);
      } else {
        setMessages([]);
        toast.error('Failed to load conversation');
      }
    } finally {
      setLoadingSession(false);
    }
  };

  const newSession = () => {
    setActiveId(null);
    setMessages([]);
    setInput('');
  };

  const deleteSession = async (sid) => {
    if (!window.confirm('Delete this conversation?')) return;
    const res = await authFetch(`${API}/assist-me/sessions/${sid}`, { method: 'DELETE' });
    if (res.ok) {
      toast.success('Conversation deleted');
      if (activeId === sid) newSession();
      loadSessions();
    } else toast.error('Delete failed');
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content, ts: new Date().toISOString() }]);
    setSending(true);
    try {
      const res = await authJsonFetch(`${API}/assist-me/chat`, {
        method: 'POST',
        body: JSON.stringify({
          message: content,
          session_id: activeId,
          model_choice: modelChoice,
        }),
      });
      if (!res.ok) {
        // Surface the real backend error inside the chat so the operator
        // sees *why* instead of the generic "see toast above" message.
        const err = await res.json().catch(() => ({}));
        const reason = err.detail || `HTTP ${res.status}`;
        toast.error(reason);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `I couldn't answer that — **${reason}**\n\nTry rephrasing, or ping an admin if this keeps happening.`,
          ts: new Date().toISOString(),
        }]);
        return;
      }
      const data = await res.json();
      setActiveId(data.session_id);
      if (data.model_info) setModelInfo(data.model_info);
      const toolCalls = data.tool_calls || [];
      // Toast when knowledge was just persisted so the user knows it stuck
      const taughtCalls = toolCalls.filter(tc => tc.tool === 'remember');
      if (taughtCalls.length > 0) {
        toast.success(`Saved ${taughtCalls.length} new ${taughtCalls.length === 1 ? 'rule' : 'rules'} to memory`);
      }
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.reply,
        ts: new Date().toISOString(),
        tool_calls: toolCalls,
      }]);
      loadSessions();
    } catch (err) {
      toast.error('Network error');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Network error: ${err?.message || 'request failed'}. Please try again.`,
        ts: new Date().toISOString(),
      }]);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // ============= MEMORY / LEARNED KNOWLEDGE =============
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [memoryRows, setMemoryRows] = useState([]);
  const [memoryLoading, setMemoryLoading] = useState(false);
  const [newTopic, setNewTopic] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newScope, setNewScope] = useState('user');

  const loadMemory = useCallback(async () => {
    setMemoryLoading(true);
    try {
      const res = await authFetch(`${API}/assist-me/knowledge?limit=200`);
      if (res.ok) {
        const data = await res.json();
        setMemoryRows(data.rows || []);
      } else {
        toast.error('Failed to load memory');
      }
    } finally {
      setMemoryLoading(false);
    }
  }, []);

  const openMemory = () => {
    setMemoryOpen(true);
    loadMemory();
  };

  const addMemory = async () => {
    const topic = newTopic.trim();
    const content = newContent.trim();
    if (!topic || !content) {
      toast.error('Topic and content are required');
      return;
    }
    const res = await authJsonFetch(`${API}/assist-me/knowledge`, {
      method: 'POST',
      body: JSON.stringify({ topic, content, scope: newScope }),
    });
    if (res.ok) {
      toast.success('Saved to memory');
      setNewTopic('');
      setNewContent('');
      loadMemory();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Save failed');
    }
  };

  const removeMemory = async (knowledge_id) => {
    if (!window.confirm('Delete this memory entry?')) return;
    const res = await authFetch(`${API}/assist-me/knowledge/${knowledge_id}`, { method: 'DELETE' });
    if (res.ok) {
      toast.success('Removed');
      loadMemory();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Delete failed');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="assist-me-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Sparkles className="w-7 h-7" />
          Assist Me
        </h1>
        <p className="text-muted-foreground mt-1">
          Conversational AI that runs <strong>live read-only queries</strong> against your booking, shoot, edit, print, dispatch, accounting and feedback data — answers are always up-to-date.
          Assist Me cannot send emails or change data on your behalf.
        </p>
      </div>

      <div className="grid grid-cols-12 gap-4 h-[calc(100vh-220px)]">
        {/* Conversations list */}
        <div className="col-span-12 md:col-span-3">
          <Card className="h-full flex flex-col">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Conversations</CardTitle>
                <Button size="sm" variant="outline" onClick={newSession} data-testid="assist-new-chat-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> New
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto px-2 space-y-1">
              {sessions.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No conversations yet.</p>
              ) : sessions.map(s => (
                <div
                  key={s.session_id}
                  className={`group flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted ${activeId === s.session_id ? 'bg-muted' : ''}`}
                  onClick={() => openSession(s.session_id)}
                  data-testid={`assist-session-${s.session_id}`}
                >
                  <MessageCircle className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{s.title || 'Untitled'}</p>
                    <p className="text-[10px] text-muted-foreground">{s.message_count || 0} msgs</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }}
                    className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0"
                  >
                    <Trash2 className="w-3 h-3 text-destructive" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Chat area */}
        <div className="col-span-12 md:col-span-9">
          <Card className="h-full flex flex-col">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                {activeId ? sessions.find(s => s.session_id === activeId)?.title || 'Conversation' : 'New conversation'}
                <Badge variant="outline" className="text-[10px] ml-auto">
                  <Lock className="w-2.5 h-2.5 mr-1" /> Read-only
                </Badge>
                {/* On-demand model switcher — operator can pick a faster
                    model if hitting 504s on a slow one. Selection
                    persists per-browser. */}
                <Select value={modelChoice} onValueChange={setModelChoice}>
                  <SelectTrigger className="w-52 h-7 text-xs" data-testid="assist-model-select">
                    <SelectValue placeholder="Model" />
                  </SelectTrigger>
                  <SelectContent>
                    {modelOptions.map(opt => (
                      <SelectItem key={opt.id} value={opt.id} data-testid={`assist-model-option-${opt.id}`}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openMemory}
                  className="h-7 text-xs"
                  data-testid="assist-memory-btn"
                  title="View / edit what Assist Me has learned"
                >
                  <Brain className="w-3.5 h-3.5 mr-1" /> Memory
                </Button>
              </CardTitle>
              <CardDescription className="text-xs">
                {modelInfo ? (
                  <>
                    Powered by <code className="text-[10px] bg-muted px-1 rounded">{modelInfo.provider}/{modelInfo.model}</code>
                    {modelInfo.temperature != null ? ` · temp ${modelInfo.temperature}` : ''}
                    {modelInfo.source === 'user_override' ? ' · override' : ''}
                    {' · Sydney TZ'}
                  </>
                ) : (
                  <>Powered by your configured AI provider. Sydney TZ.</>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col gap-3 min-h-0">
              <div className="relative flex-1 min-h-0">
                <div
                  ref={scrollRef}
                  onScroll={onTranscriptScroll}
                  className="absolute inset-0 overflow-y-auto space-y-3 pr-2"
                  data-testid="assist-transcript"
                >
                {loadingSession ? (
                  <div className="flex items-center justify-center py-10 text-muted-foreground gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
                ) : messages.length === 0 ? (
                  <div className="py-10 px-6 text-center">
                    <Lightbulb className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground mb-4">Ask anything about your business data. A few starters:</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {SUGGESTIONS.map(s => (
                        <Button key={s} variant="outline" size="sm" onClick={() => send(s)} className="text-xs h-auto py-1.5 whitespace-normal max-w-xs">
                          {s}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`rounded-lg px-3 py-2 max-w-[80%] text-sm whitespace-pre-wrap ${
                        m.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                      data-testid={`assist-msg-${m.role}-${i}`}
                    >
                      {m.content}
                      {m.role === 'assistant' && Array.isArray(m.tool_calls) && m.tool_calls.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-border/50 flex flex-wrap gap-1">
                          <span className="text-[10px] text-muted-foreground mr-1">Live queries run:</span>
                          {m.tool_calls.map((tc, j) => (
                            <Badge key={j} variant="outline" className="text-[10px] py-0 px-1.5">
                              {tc.tool}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="rounded-lg px-3 py-2 bg-muted text-sm flex items-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" /> Thinking…
                    </div>
                  </div>
                )}
              </div>

              {/* Floating "↓ New messages" pill — appears only when the
                  operator has scrolled away from the bottom AND new
                  content has arrived. Click jumps back to the latest. */}
              {hasNewBelow && (
                <button
                  type="button"
                  onClick={() => { setAutoFollow(true); scrollToBottom('smooth'); }}
                  className="absolute left-1/2 -translate-x-1/2 bottom-3 z-20 bg-primary text-primary-foreground rounded-full px-3 py-1 text-xs shadow-md hover:opacity-90"
                  data-testid="assist-jump-to-latest"
                >
                  ↓ New messages
                </button>
              )}
              </div>

              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Ask Assist Me…"
                  disabled={sending}
                  data-testid="assist-input"
                />
                <Button onClick={() => send()} disabled={sending || !input.trim()} data-testid="assist-send-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Memory / Learned Knowledge dialog */}
      <Dialog open={memoryOpen} onOpenChange={setMemoryOpen}>
        <DialogContent className="max-w-2xl" data-testid="assist-memory-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-4 h-4" /> Assist Me Memory
            </DialogTitle>
            <DialogDescription>
              Business rules, glossary, and instructions Assist Me uses to answer your questions.
              <strong> Personal</strong> entries are visible only to you; <strong>Global</strong> entries
              (admin-only) apply to everyone. Assist Me also auto-saves anything you explicitly teach in chat.
            </DialogDescription>
          </DialogHeader>

          {/* Add new */}
          <div className="border rounded-md p-3 space-y-2 bg-muted/30">
            <p className="text-xs font-semibold">Add a new memory entry</p>
            <Input
              placeholder="Topic (e.g. 'PrePak shorthand')"
              value={newTopic}
              onChange={(e) => setNewTopic(e.target.value)}
              maxLength={120}
              data-testid="memory-topic-input"
            />
            <Textarea
              placeholder="Content (e.g. 'When the operator says PrePak, treat it as Pre-school Package')"
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              maxLength={4000}
              rows={3}
              data-testid="memory-content-input"
            />
            <div className="flex items-center gap-2">
              <Select value={newScope} onValueChange={setNewScope}>
                <SelectTrigger className="w-40 h-8 text-xs" data-testid="memory-scope-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Personal (just me)</SelectItem>
                  <SelectItem value="global">Global (everyone)</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" onClick={addMemory} data-testid="memory-add-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Save
              </Button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-[40vh] overflow-y-auto space-y-2 mt-2">
            {memoryLoading ? (
              <div className="flex items-center justify-center py-6 text-muted-foreground text-sm gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : memoryRows.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">
                No memory entries yet. Teach Assist Me by chatting or use the form above.
              </p>
            ) : (
              memoryRows.map(row => (
                <div
                  key={row.knowledge_id}
                  className="border rounded-md p-2.5 text-sm flex items-start gap-2"
                  data-testid={`memory-row-${row.knowledge_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={row.scope === 'global' ? 'default' : 'outline'} className="text-[10px]">
                        {row.scope === 'global' ? 'Global' : 'Personal'}
                      </Badge>
                      <span className="font-medium text-xs truncate">{row.topic}</span>
                      {row.source === 'chat_taught' && (
                        <Badge variant="outline" className="text-[10px]">taught in chat</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground whitespace-pre-wrap">{row.content}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 shrink-0"
                    onClick={() => removeMemory(row.knowledge_id)}
                    data-testid={`memory-delete-${row.knowledge_id}`}
                  >
                    <X className="w-3.5 h-3.5 text-destructive" />
                  </Button>
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMemoryOpen(false)} data-testid="memory-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
